import _ from 'lodash'
import { createContext, useCallback, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import Tabs from '@/components/ui/tabs/Tabs'
import TabPanel from '@/components/ui/tab/TabPanel'

import { ExpertiseService } from '@/core/models/expertise.model'
import { useGetExpertiseQuery } from '@/core/services/expertise'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { createExternalServiceExpertise, selectExpertiseCreateRequestTracker } from '@/redux/reducers/expertise.reducer'

import { SelectDoctor } from './SelectDoctor'
import { Summary } from './Summary'
import SelectNetwork from './SelectNetwork'
import { PlanMeeting } from './PlanMeeting'

export function ExpertiseValidationPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { expertiseId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: expertise, isLoading: isFetchingExpertise } = useGetExpertiseQuery(expertiseId ?? skipToken)

  const service = useMemo(() => _.get(location.state, 'service', ExpertiseService.MedConnect), [location])

  const [activeStep, setActiveStep] = useState(0)

  const createOmnidocTeleExpertiseRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.expertise.createOnOmnidoc.success'), { variant: 'success' })
      if (expertiseId) navigate(`/expertises/${expertiseId}`)
    },
    onError: () => {
      enqueueSnackbar(t('notification.expertise.createOnOmnidoc.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const handleValidate = () => {
    switch (service) {
      case ExpertiseService.MedConnect:
        setActiveStep((prevStep) => prevStep + 1)
        break
      case ExpertiseService.Omnidoc:
        if (expertiseId) createOmnidocTeleExpertiseRequestHandler.dispatch(createExternalServiceExpertise(expertiseId, ExpertiseService.Omnidoc))
        break
      default:
        break
    }
  }

  if (isFetchingExpertise) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!expertise) {
    return <NotFoundPage />
  }

  return (
    <Container maxWidth="xl" sx={{ height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
      <Box pt="30px" pb="30px" height="100%">
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          {activeStep === 0 && (
            <Stack height="100%">
              <Summary expertise={expertise} service={service} />
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3}>
                <Button component={Link} to={`/expertises/${expertiseId}`} variant="neutral">
                  {t('common:button.back.label')}
                </Button>
                <Stack direction="row" alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
                  <LoadingButton
                    data-testid="expertise-validate-submit"
                    color="success"
                    loading={createOmnidocTeleExpertiseRequestHandler.requestTracker.loading}
                    onClick={handleValidate}
                    variant="contained"
                  >
                    {t('page.expertiseValidation.buttonValidate.label')}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          )}
          {activeStep === 1 && (
            <Stack height="100%">
              <Tabs
                centered
                tabs={[
                  { id: 'doctors', label: t('page.expertise.selectDoctorStep.title') },
                  { id: 'networks', label: t('page.expertise.selectDoctorStep.network.title') },
                  { id: 'meeting', label: t('page.expertise.selectDoctorStep.planMeeting.title') },
                ]}
                renderTabPanel={({ value }) => (
                  <Box sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                    <TabPanel value={value} index={0} sx={{ height: '100%' }} unmountOnExit>
                      <SelectDoctor expertiseId={expertise._id} setActiveStep={setActiveStep} />
                    </TabPanel>
                    <TabPanel value={value} index={1} sx={{ height: '100%' }} unmountOnExit>
                      <SelectNetwork expertiseId={expertise._id} setActiveStep={setActiveStep} />
                    </TabPanel>
                    <TabPanel value={value} index={2} sx={{ height: '100%' }} unmountOnExit>
                      <PlanMeeting expertiseId={expertise._id} patientId={expertise.patient._id} setActiveStep={setActiveStep} />
                    </TabPanel>
                  </Box>
                )}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                variant="fullWidth"
              />
            </Stack>
          )}
        </Card>
      </Box>
    </Container>
  )
}

export default ExpertiseValidationPage
